import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { DocumentSelector } from './DocumentSelector';
import { DocumentSelection } from '@/types';
import { Document } from '@/hooks/useDocuments';

interface DocumentSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterSlug: string;
  documentSelections: Map<string, DocumentSelection>;
  onToggleSelection: (doc: Document) => void;
  onClearSelection: () => void;
  // Upload
  onUploadComplete: (fileNames?: string[]) => void;
  // Focus management
  chatInputRef?: React.RefObject<HTMLTextAreaElement | null>;
  onDialogClose?: () => void;
}

/**
 * Full-screen dialog wrapper for DocumentSelector.
 * Replaces the previous DocumentSelectorDialog in Case Partner.
 */
export const DocumentSelectorDialog: React.FC<DocumentSelectorDialogProps> = ({
  open,
  onOpenChange,
  matterSlug,
  documentSelections,
  onToggleSelection,
  onClearSelection,
  onUploadComplete,
  chatInputRef,
  onDialogClose,
}) => {
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      // Blur the chat input when dialog opens to prevent aria-hidden conflict
      if (chatInputRef?.current) {
        chatInputRef.current.blur();
      }
    } else {
      // Dialog is closing
      onDialogClose?.();
    }
    onOpenChange(newOpen);
  };

  const handleDone = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-gray-200 shrink-0">
          <DialogTitle className="text-lg font-semibold">Select Documents</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            Browse folders and select documents for context.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          <DocumentSelector
            matterSlug={matterSlug}
            documentSelections={documentSelections}
            onToggleSelection={onToggleSelection}
            onClearSelection={onClearSelection}
            showUploadButton={true}
            onUploadComplete={onUploadComplete}
            onDone={handleDone}
            className="h-full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentSelectorDialog;
